import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import { usePageMeta } from "@/lib/seo";

const FIELDS = [
  { key: "name", label: "Your name", type: "text", required: true },
  { key: "email", label: "Your email", type: "email", required: true },
  { key: "whatsapp", label: "WhatsApp number (with country code)", type: "text", required: true },
];

export default function Mentorship() {
  usePageMeta(
    "The Wealth Blueprint Mentorship — By Application | Just be you by Mehjabin",
    "A private 1:1 container for rebuilding your capacity to hold wealth, love and growth. Seven sessions and beyond. By application only."
  );
  const [form, setForm] = useState({ name: "", email: "", whatsapp: "", why_now: "", building: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/mentorship/apply", form);
      setDone(true);
    } catch {
      toast.error("Something interrupted that. Try again?");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="mentorship-page">
      <section className="max-w-3xl mx-auto px-6 pt-16 md:pt-24 pb-10 text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
          <div className="overline mb-5">Private 1:1 container · by application only</div>
          <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl text-ink leading-[1.06] tracking-tight">
            The Wealth Blueprint<br /><em className="text-peach-deep">Mentorship</em>
          </h1>
          <p className="font-serif italic text-lg md:text-xl text-ink/60 mt-6 max-w-xl mx-auto leading-relaxed">
            Naming the pattern is the first layer. This is where it gets cleared — in real time, with someone tracking it beside you.
          </p>
        </motion.div>
      </section>

      <section className="max-w-3xl mx-auto px-6 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="glass-strong rounded-[1.5rem] p-8">
            <div className="overline mb-4">This is for you if</div>
            <ul className="space-y-3 text-sm text-ink/75 font-sans leading-relaxed">
              {[
                "You've named your archetype — and you're done just noticing it",
                "Something you're building matters enough to clear what's in its way",
                "You want one person holding the whole map with you, not another course",
                "You're ready to be honest about money, love and capacity in the same room",
              ].map((t) => (
                <li key={t} className="flex gap-2.5"><Check size={15} className="text-sage-deep mt-0.5 flex-shrink-0" strokeWidth={2} />{t}</li>
              ))}
            </ul>
          </div>
          <div className="glass-strong rounded-[1.5rem] p-8">
            <div className="overline mb-4">Inside the container</div>
            <ul className="space-y-3 text-sm text-ink/75 font-sans leading-relaxed">
              {[
                "Seven private 1:1 sessions — and the container stays open until the shift holds",
                "Your archetype traced to its ancestral & karmic roots, then rebuilt",
                "Voice-note access between sessions — you're held through the week, not just in it",
                "Rituals & practices shaped around your season, not a template",
              ].map((t) => (
                <li key={t} className="flex gap-2.5"><Check size={15} className="text-sage-deep mt-0.5 flex-shrink-0" strokeWidth={2} />{t}</li>
              ))}
            </ul>
          </div>
        </div>
        <div className="mt-5 bg-ink rounded-[1.5rem] p-8 text-center" data-testid="mentorship-investment">
          <div className="text-amber text-[10px] uppercase tracking-[0.25em] font-sans font-semibold mb-3">The investment</div>
          <div className="font-serif text-canvas text-3xl">₹3,00,000</div>
          <p className="text-canvas/60 text-xs font-sans mt-3 max-w-md mx-auto leading-relaxed">
            Payment plans available. Two places open each month — every application is read personally, and not every application is accepted. That's what keeps the room sacred.
          </p>
        </div>
      </section>

      <section className="max-w-xl mx-auto px-6 pb-24">
        {done ? (
          <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} className="glass-strong rounded-[1.5rem] p-10 text-center" data-testid="mentorship-success">
            <Check size={28} className="text-sage-deep mx-auto mb-4" strokeWidth={1.5} />
            <div className="font-serif text-2xl text-ink">Your application is with me. 🌙</div>
            <p className="text-ink/60 text-sm font-sans mt-3 leading-relaxed">
              I read each one personally. If it feels like the right fit, you'll hear from me within 48 hours — check your inbox for a small confirmation.
            </p>
          </motion.div>
        ) : (
          <form onSubmit={submit} className="glass-strong rounded-[1.5rem] p-8 md:p-10 space-y-4" data-testid="mentorship-form">
            <div className="text-center mb-2">
              <div className="overline mb-2">The application</div>
              <p className="text-ink/55 text-xs font-sans">Five minutes, honestly answered, is all it takes.</p>
            </div>
            {FIELDS.map((f) => (
              <input
                key={f.key}
                type={f.type}
                required={f.required}
                value={form[f.key]}
                onChange={set(f.key)}
                placeholder={f.label}
                className="w-full bg-white/70 border border-ink/10 rounded-full px-6 py-3.5 text-ink placeholder:text-ink/40 font-sans text-sm focus:outline-none focus:border-peach-deep transition-colors"
                data-testid={`mentorship-${f.key}-input`}
              />
            ))}
            <textarea
              required
              value={form.why_now}
              onChange={set("why_now")}
              placeholder="Why now? What made you apply today, honestly?"
              rows={3}
              className="w-full bg-white/70 border border-ink/10 rounded-2xl px-6 py-4 text-ink placeholder:text-ink/40 font-sans text-sm focus:outline-none focus:border-peach-deep transition-colors resize-none"
              data-testid="mentorship-why-input"
            />
            <textarea
              required
              value={form.building}
              onChange={set("building")}
              placeholder="What are you building — in wealth, love or life — that this pattern is standing in front of?"
              rows={3}
              className="w-full bg-white/70 border border-ink/10 rounded-2xl px-6 py-4 text-ink placeholder:text-ink/40 font-sans text-sm focus:outline-none focus:border-peach-deep transition-colors resize-none"
              data-testid="mentorship-building-input"
            />
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-lapis hover:bg-lapis-deep text-canvas font-sans font-semibold text-sm uppercase tracking-[0.14em] rounded-full px-6 py-4 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
              data-testid="mentorship-submit-btn"
            >
              {loading ? "Sending…" : "Send my application"}
              {!loading && <ArrowRight size={16} strokeWidth={2} />}
            </button>
            <p className="text-ink/40 text-xs font-sans text-center pt-1">
              Applying isn't committing. It's opening a door for a real conversation.
            </p>
          </form>
        )}
      </section>
    </div>
  );
}
